import {
	Component, ElementRef, model, OnInit,
	output, signal, ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';

import moment from 'moment';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import {
	OwlDateTimeModule, OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { NgSelectModule } from '@ng-select/ng-select';

export interface DateRange {
	start: string | null;
	end: string | null;
}

interface DateRangePreset {
	label: string;
	interval: string | null;
	dateRange: DateRange;
	selected?: boolean;
}

const anyTimeLabel = 'Any Time';

@Component({
	selector: 'app-date-range-dropdown',
	templateUrl: './date-range-dropdown.component.html',
	styleUrl: './date-range-dropdown.component.scss',
	standalone: true,
	imports: [
		OwlNativeDateTimeModule, OwlDateTimeModule,
		FaIconComponent, FormsModule, NgSelectModule,
	],
})
export class DateRangeDropdownComponent implements OnInit {
	@ViewChild('dropdownList') dropdownList!: ElementRef;

	dateRange = model<DateRange>({ start: null, end: null });
	dateRangeDisplay = signal<string>('');
	selectedDateRange = signal<[Date | null, Date | null]>([null, null]);

	dateRangeChange = output<DateRange>();

	dateRangePresets: DateRangePreset[] = [
		{
			label: anyTimeLabel,
			interval: null,
			dateRange: { start: null, end: null },
		},
		{
			label: 'Today',
			interval: 'day',
			dateRange: {
				start: moment().format('YYYY-MM-DD'),
				end: moment().format('YYYY-MM-DD'),
			},
		},
		{
			label: 'Yesterday',
			interval: 'day',
			dateRange: {
				start: moment().subtract(1, 'day').format('YYYY-MM-DD'),
				end: moment().subtract(1, 'day').format('YYYY-MM-DD'),
			},
		},
		{
			label: 'This Week',
			interval: 'week',
			dateRange: {
				start: moment().startOf('week').format('YYYY-MM-DD'),
				end: moment().endOf('week').format('YYYY-MM-DD'),
			},
		},
		{
			label: 'Last Week',
			interval: 'week',
			dateRange: {
				start: moment()
					.subtract(1, 'week')
					.startOf('week')
					.format('YYYY-MM-DD'),
				end: moment()
					.subtract(1, 'week')
					.endOf('week')
					.format('YYYY-MM-DD'),
			},
		},
		{
			label: 'This Month',
			interval: 'month',
			dateRange: {
				start: moment().startOf('month').format('YYYY-MM-DD'),
				end: moment().endOf('month').format('YYYY-MM-DD'),
			},
		},
		{
			label: 'Last Month',
			interval: 'month',
			dateRange: {
				start: moment()
					.subtract(1, 'month')
					.startOf('month')
					.format('YYYY-MM-DD'),
				end: moment()
					.subtract(1, 'month')
					.endOf('month')
					.format('YYYY-MM-DD'),
			},
		},
		{
			label: 'This Quarter',
			interval: 'quarter',
			dateRange: {
				start: moment().startOf('quarter').format('YYYY-MM-DD'),
				end: moment().endOf('quarter').format('YYYY-MM-DD'),
			},
		},
		{
			label: 'Last Quarter',
			interval: 'quarter',
			dateRange: {
				start: moment()
					.subtract(1, 'quarter')
					.startOf('quarter')
					.format('YYYY-MM-DD'),
				end: moment()
					.subtract(1, 'quarter')
					.endOf('quarter')
					.format('YYYY-MM-DD'),
			},
		},
		{
			label: 'This Year',
			interval: 'year',
			dateRange: {
				start: moment().startOf('year').format('YYYY-MM-DD'),
				end: moment().endOf('year').format('YYYY-MM-DD'),
			},
		},
		{
			label: 'Last Year',
			interval: 'year',
			dateRange: {
				start: moment()
					.subtract(1, 'year')
					.startOf('year')
					.format('YYYY-MM-DD'),
				end: moment()
					.subtract(1, 'year')
					.endOf('year')
					.format('YYYY-MM-DD'),
			},
		},
	];

	// icons
	faCheck = signal(faCheck);

	ngOnInit() {
		this.onDateRangeChange([
			this.dateRange().start ? new Date(this.dateRange().start!) : null,
			this.dateRange().end ? new Date(this.dateRange().end!) : null,
		]);
		this.setDateRangeDisplayValue();
	}

	onDateRangeChange(selection: [Date | null, Date | null]) {
		const dateRangePreset = this.findDateRangePresetByDate({
			start: selection[0] ? formatDate(selection[0], 'yyyy-MM-dd', 'en-US') : null,
			end: selection[1] ? formatDate(selection[1], 'yyyy-MM-dd', 'en-US') : null,
		});
		if (dateRangePreset) {
			this.onSelectDateRangePreset(dateRangePreset);
		} else if (!selection[0] && !selection[1]) {
			this.onSelectDateRangePreset(this.dateRangePresets[0]);
		}
	}

	onSelectDateRangePreset(preset: DateRangePreset) {
		this.dateRangePresets.forEach((p) => (p.selected = false));
		preset.selected = true;
		this.onSelectDateRange([
			preset.dateRange.start ? new Date(preset.dateRange.start!) : null,
			preset.dateRange.end ? new Date(preset.dateRange.end!) : null,
		]);
	}

	onSelectDateRangePresetByLabel(event: Event) {
		const label = (event.target as HTMLInputElement).value;
		const preset = this.dateRangePresets.find((p) => p.label === label);
		if (preset) {
			this.onSelectDateRangePreset(preset);
		}
	}

	onApply() {
		this.dateRange.set({
			start: this.selectedDateRange()[0] ? moment(this.selectedDateRange()[0]).format('YYYY-MM-DD') : null,
			end: this.selectedDateRange()[1] ? moment(this.selectedDateRange()[1]).format('YYYY-MM-DD') : null,
		});
		this.setDateRangeDisplayValue();
		this.dateRangeChange.emit(this.dateRange());
	}

	clearFilter() {
		this.onSelectDateRange([null, null]);
		this.onApply();
	}

	closeDropdown(event: Event) {
		event.stopPropagation();
		this.dropdownList?.nativeElement?.classList.remove('show');
	}

	private onSelectDateRange(selection: [Date | null, Date | null]) {
		this.selectedDateRange.set([
			selection[0] ? moment(selection[0]).startOf('day').toDate() : null,
			selection[1] ? moment(selection[1]).endOf('day').toDate() : null,
		]);
	}

	private getDateOnly(date: string) {
		return formatDate(date, 'yyyy-MM-dd', 'en-US');
	}

	private findDateRangePresetByDate(
		dateRange: DateRange,
	): DateRangePreset | null {
		if (dateRange.start && dateRange.end) {
			const match = this.dateRangePresets.find(
				(p) =>
					p.dateRange.start &&
					p.dateRange.end &&
					this.getDateOnly(p.dateRange.start) ===
						this.getDateOnly(dateRange.start!) &&
					this.getDateOnly(p.dateRange.end) ===
						this.getDateOnly(dateRange.end!),
			);
			if (match) return match;
		}
		if (!dateRange.start && !dateRange.end) {
			return (
				this.dateRangePresets.find((p) => p.label === anyTimeLabel) ??
				null
			);
		}
		return this.dateRangePresets.find((p) => p.label === 'Custom') ?? null;
	}

	private setDateRangeDisplayValue() {
		const start = this.dateRange().start
			? new Date(this.dateRange().start!)
			: null;
		const end = this.dateRange().end
			? new Date(this.dateRange().end!)
			: null;

		if (!start && !end) {
			this.dateRangeDisplay.set(anyTimeLabel);
		} else if (start && !end) {
			this.dateRangeDisplay.set(
				`From ${formatDate(start!, 'MMM d, yyyy', 'en-US')}`,
			);
		} else if (!start && end) {
			this.dateRangeDisplay.set(
				`To ${formatDate(end!, 'MMM d, yyyy', 'en-US')}`,
			);
		} else if (start && end && this.getDateOnly(start.toISOString()) === this.getDateOnly(end.toISOString())) {
			this.dateRangeDisplay.set(
				formatDate(start!, 'MMM d, yyyy', 'en-US'),
			);
		} else {
			this.dateRangeDisplay.set(
				`${formatDate(start!, 'MMM d, yyyy', 'en-US')} - ${formatDate(end!, 'MMM d, yyyy', 'en-US')}`,
			);
		}
	}
}
